<template>
  <div class="modal-overflow">
    <div class="row">
      <div class="col-md-8">
        <table style="width:100%" class="table-user-data">
          <colgroup>
            <col style="width:50%">
            <col style="width:50%">
          </colgroup>
          <tr class="table-header">
            <td>Property</td>
            <td>Value</td>
          </tr>
          <tr>
            <td>ID</td>
            <td>{{ data.id }}</td>
          </tr>
          <tr>
            <td>Name</td>
            <td>{{ data.name }}</td>
          </tr>
          <tr>
            <td>Email</td>
            <td>{{ data.email.toLowerCase() }}</td>
          </tr>
          <tr>
            <td>Status</td>
            <td>{{ data.active | printBool }}</td>
          </tr>
        </table>
      </div>
      <div class="col-md-4" style="padding-right: 25px;">
        <h6>Groups</h6>
        <tag type="primary" size="large" v-for="group in data.groups" :key="group"><span style="color: white; font-weight: bold;">{{group}}</span></tag>
      </div>
      <div class="col-md-12">
        <h4>Preferences</h4>
        <table style="width:100%" class="table-user-preference-data">
          <colgroup>
            <col style="width:30%">
            <col style="width:30%">
            <col style="width:40%">
          </colgroup>
          <tr class="table-header">
            <td>Preference</td>
            <td>Preference Key</td>
            <td>Value</td>
          </tr>
          <tr v-if="preference.active" v-for="preference in globalUserPreferences">
            <td>
              {{ preference.name }}
            </td>
            <td>
              {{preference.key}}
            </td>
            <td>
              <div v-if="!preference.inputType || preference.inputType === constants.COMPONENTS.INPUT">
                <el-input :data-vv-as="preference.name"
                          :disabled="!canEdit"
                          :name="preference.name"
                          v-model="editUser.preferences[constants.USER_PREFERENCE_TYPES.GLOBAL][preference.key]"
                          class="fifty-percent" />
              </div>
              <div v-else-if="preference.inputType === constants.COMPONENTS.SELECT">
                <el-select :data-vv-as="preference.name"
                           :disabled="!canEdit"
                           clearable
                           :name="preference.name"
                           v-model="editUser.preferences[constants.USER_PREFERENCE_TYPES.GLOBAL][preference.key]"
                           style="width: 100%;">
                  <el-option v-for="option in preference.values"
                             class="select"
                             :value="option.value"
                             :label="option.label"
                             :key="option.value">
                  </el-option>
                </el-select>
              </div>
              <div v-else-if="preference.inputType === constants.COMPONENTS.FILE" class="row">
                <div class="col-md-7" style="padding-top: 12px;">
                  <input :data-vv-as="preference.name"
                         :disabled="!canEdit"
                         :name="preference.name"
                         :id="preference.key"
                         @change="setFile(preference.key)"
                         accept=".jpg,.jpeg,.png"
                         type="file">
                </div>
                <div class="col-md-4" style="padding-top: 15px;">
                  <span style="font-style: italic;">
                    {{ editUser.preferences[constants.USER_PREFERENCE_TYPES.GLOBAL][preference.key] }}
                  </span>
                </div>
                <div class="col-md-1">
                  <p-button class="pull-right" :disabled="isDeleteDisabled(preference.key)" type="danger" size="sm" icon @click="flagForDeletion(preference.key)">
                    <i class="fa fa-times"></i>
                  </p-button>
                </div>
              </div>
            </td>
          </tr>
        </table>
        <div class="form-group" v-if="canEdit">
          <button type="button" :disabled="isSubmitDisabled" @click="validateAndSubmit" class="btn btn-fill btn-info btn-wd btn-save-user-preferences pull-right">Save User Preferences</button>
        </div>
        <div v-if="isSuperAdmin">
          <button
          type="button"
          @click="editUser.active ? deactivate() : activate()"
          :class="['btn', 'btn-fill', editUser.active ? 'btn-danger' : 'btn-success', 'btn-wd', 'btn-activate-deactivate', 'pull-left', 'btn-fill']"
        >
          {{ editUser.active ? 'Deactivate User' : 'Activate User' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import {
    notifySuccess,
    notifyError,
    userPreferencesMergePatchPayload,
    parseUsernameFromEmail,
    clearLocalStorage
  } from 'src/utilities/utils'
  import Constants from 'src/utilities/constants'
  import Permissions from 'src/utilities/permissions'
  import {Tag} from 'element-ui'
  import {sync} from 'vuex-pathify'
  import {ACCEPTED, NO_CONTENT, NOT_FOUND, OK} from 'http-status-codes'
  import {mapGetters} from 'vuex'

  export default {
    props: ['data'],
    components: {
      Tag
    },
    data() {
      return {
        constants: Constants,
        fileInputs: [],
        filesToDelete: [],
        isProcessing: false,
        newUser: false,
        groups: [],
      }
    },
    computed: {
      ...mapGetters({
        user: 'security/user',
        userId: 'security/userId'
      }),
      editUser: sync('users@user'),
      globalUserPreferences() {
        return this.$store.get('users/allUserPreferencesByType', Constants.USER_PREFERENCE_TYPES.GLOBAL)
      },
      canEdit() {
        return this.$can('edit', Permissions.USERS)
      },
      isSuperAdmin() {
        return this.$store.get('security/isInGroup', Constants.GROUPS.SUPER_USERS)
      },
      isSubmitDisabled() {
        return this.isProcessing
      }
    },
    methods: {
      getError(fieldName) {
        return this.errors.first(fieldName)
      },
      isDeleteDisabled(preferenceKey) {
        return !this.editUser.preferences[Constants.USER_PREFERENCE_TYPES.GLOBAL][preferenceKey]
      },
      async validateAndSubmit() {
        this.isProcessing = true

        if (await this.$validator.validateAll()) {
          let response = null
          const payload = userPreferencesMergePatchPayload(this.editUser)
          if (this.newUser) {
            // user hasn't logged in yet but admin user is setting up their global preferences
            response = await this.$store.dispatch('put', payload)
          } else {
            response = await this.$store.dispatch('mergePatch', payload)
          }

          if (response.result === Constants.RETURN_CODES.SUCCESS) {
            this.clearCacheLocalStorage()
            await this.deleteFiles()
            await this.uploadFiles()

            // is the currently logged in user editing their own record?
            if (this.userId === this.data.id) {
              this.$store.commit('security/updateGlobalUserPreferences', payload.data.preferences.global)
            }

            notifySuccess(this, 'User Preferences Updated Successfully')
            this.$eventHub.$emit('userUpdated')
            this.$emit('close')
            return
          }

          notifyError(this, 'Failed to Update User Preferences')
        }
        this.isProcessing = false
      },
      async deleteFiles() {
        if (this.filesToDelete.length > 0) {
          for (const fileToDelete of this.filesToDelete) {
            const deleteResponse = await this.$store.dispatch('files/delete', fileToDelete)

            if (NO_CONTENT !== deleteResponse.status) {
              notifyError(this, 'Failed to delete: ' + fileToDelete.file)
            }
          }
        }
      },
      userToLowerCase() {
        const user = parseUsernameFromEmail(this.editUser.email)
        return user.toLowerCase();
      },
      async deactivate() {
        const response = await this.$store.dispatch('users/updateUserIsActive', {
          id: this.data.id,
          active: false
        })

        if (OK === response.status) {
          notifySuccess(this, 'User deactivated successfully')
          return
        }

        notifyError(this, 'Failed to deactivate user')
      },
      async activate() {
        const response = await this.$store.dispatch('users/updateUserIsActive', {
          id: this.data.id,
          active: true
        })

        if (OK === response.status) {
          notifySuccess(this, 'User activated successfully')
          return
        }

        notifyError(this, 'Failed to activate user')
      },
      async uploadFiles() {
        if (this.fileInputs.length > 0) {
          const files = []

          for (const fileInput of this.fileInputs) {
            files.push(...fileInput.files)
          }

          const uploadResponse = await this.$store.dispatch('files/uploadUserFiles', {
            path: this.userToLowerCase(),
            files: files
          })

          if (ACCEPTED !== uploadResponse.status) {
            notifyError('Filed to upload one or more user files.')
          }
        }
      },
      async loadUserPreferences() {
        const payload = {
          userId: this.data.id,
          preferenceType: Constants.USER_PREFERENCE_TYPES.GLOBAL
        }
        const response = await this.$store.dispatch('users/fetchUserPreferences', payload)

        if (NOT_FOUND === response.status) {
          this.newUser = true
        }
      },
      setFile(preferenceKey) {
        let fileInput = document.querySelector('#' + preferenceKey)
        if (fileInput.files) {
          this.fileInputs.push(fileInput)
          this.editUser.preferences[Constants.USER_PREFERENCE_TYPES.GLOBAL][preferenceKey] = fileInput.files[0].name
        }
      },
      flagForDeletion(preferenceKey) {
        const fileToDelete = this.user.preferences[Constants.USER_PREFERENCE_TYPES.GLOBAL][preferenceKey]

        let fileInput = document.querySelector('#' + preferenceKey)
        if (fileInput.files.length > 0) {
          fileInput.value = ''
        }

        this.filesToDelete.push({
          path: 'users/' + this.userToLowerCase(),
          name: fileToDelete
        })

        this.editUser.preferences[Constants.USER_PREFERENCE_TYPES.GLOBAL][preferenceKey] = null
      },
      clearCacheLocalStorage() {
        // purge the users cache and local storage after a user has been edited
        if (this.$store.cache.has('users/fetchAll')) {
          this.$store.cache.delete('users/fetchAll')
        }
        clearLocalStorage(Constants.LOCAL_STORAGE.LOCATION.USER_DATA)
        clearLocalStorage(Constants.LOCAL_STORAGE.LOCATION.USER_MAP_DATA)
        this.$store.set('users/allUserPreferencesLoaded', false)
      }
    },
    async beforeMount() {
      this.$store.commit('users/resetUser')
      this.$store.set('users/hydrateUser', this.data)
      await this.loadUserPreferences()
    }
  }
</script>

<style scoped>
  .table-header {
    font-weight: bold;
  }
  .table-user-data tr {
    height: 25px;
    border-bottom: 1px solid #d3dce6;
  }
  .table-user-preference-data tr {
    height: 50px;
  }
  .btn-save-user-preferences {
    margin-top: 15px;
  }
  .btn-activate-deactivate {
    margin-right: 10px;
  }
</style>
